import { useEffect, useState } from 'react';
import { hasFeature, isAtLimit, getLimits, type Tier, type TierLimits } from '@vcard/shared';
import type { BillingStatus } from '@vcard/shared';

interface TierState {
  tier: Tier;
  /** tier from the subscription alone (without admin unlocks) */
  baseTier: Tier;
  /** active admin unlock that raises the tier, if any */
  override: BillingStatus['override'];
  status: BillingStatus['status'];
  trialDaysRemaining: number | null;
  trialEndsAt: string | null;
  /** test period over and no plan: public cards show the "unpaid" notice */
  locked: boolean;
  seats: BillingStatus['seats'];
  seatTier: Tier | null;
  limits: TierLimits;
  usage: BillingStatus['usage'];
  loading: boolean;
  /** reload the status after seats or plan changed */
  refresh: () => void;
  hasFeature: (feature: string) => boolean;
  isAtLimit: (resource: keyof TierLimits, currentCount: number) => boolean;
}

export function useTier(): TierState {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    fetch('/api/billing/status', { credentials: 'include' })
      .then((r) => {
        if (!r.ok) return null;           // 401/403/5xx → treat as unauthenticated
        const ct = r.headers.get('content-type') ?? '';
        if (!ct.includes('application/json')) return null;  // guard against HTML responses
        return r.json() as Promise<BillingStatus>;
      })
      .then((data) => { if (data) setBillingStatus(data); })
      .catch(() => {})                    // silently ignore network errors
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const tier: Tier = billingStatus?.tier ?? 'free';

  return {
    tier,
    baseTier: billingStatus?.baseTier ?? tier,
    override: billingStatus?.override ?? null,
    status: billingStatus?.status ?? null,
    trialDaysRemaining: billingStatus?.trialDaysRemaining ?? null,
    trialEndsAt: billingStatus?.trialEndsAt ?? null,
    locked: billingStatus?.locked ?? false,
    seats: billingStatus?.seats ?? { starter: 0, business: 0, usedStarter: 0, usedBusiness: 0, interval: null },
    seatTier: billingStatus?.seatTier ?? null,
    limits: billingStatus?.limits ?? getLimits(tier),
    usage: billingStatus?.usage ?? { users: 0 },
    loading,
    refresh: () => setReloadKey((k) => k + 1),
    hasFeature: (feature) => hasFeature(tier, feature),
    isAtLimit: (resource, currentCount) => isAtLimit(tier, resource, currentCount),
  };
}
